// =============================================================================
// MeuDriver — useTripLive (lado aluno)
// -----------------------------------------------------------------------------
// Assina o broadcast do instrutor e mantém a posição atual + ETA.
//
// Três camadas, nesta ordem de preferência:
//   1. Broadcast   — posição ao vivo (~5 s). É o caminho normal.
//   2. trip_get_live — chamada no mount e em polling lento (20 s), para quem
//      abre a tela no meio da corrida e ainda não recebeu nenhum broadcast, e
//      para detectar mudança de estado feita pelo instrutor.
//   3. Nada — status 'nao_iniciado', a UI mostra o ponto de encontro estático.
//
// O polling existe porque broadcast não tem histórico: quem chega depois só
// veria o carro no próximo tick do instrutor.
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { getEta, type Coords, type EtaResult } from '@/lib/eta'
import { tripChannelName, type TripBroadcastPayload } from '@/hooks/useTripTracking'
import type { TripStatus } from '@/types/database'

const FALLBACK_POLL_MS = 20_000
const ETA_REFRESH_MS = 30_000

export interface LivePosition {
  lat: number
  lng: number
  headingDeg: number | null
  /** Epoch ms da leitura. Serve para a UI avisar que o sinal está velho. */
  at: number
}

interface TripLiveRow {
  status?: TripStatus
  started_at?: string | null
  arrived_at?: string | null
  boarded_at?: string | null
  position?: {
    lat: number | string
    lng: number | string
    heading_deg: number | string | null
    recorded_at: string
  } | null
}

interface UseTripLiveReturn {
  status: TripStatus
  position: LivePosition | null
  eta: EtaResult | null
  arrivedAt: string | null
  isLoading: boolean
  /** `true` quando a última posição tem mais de 60 s — sinal provavelmente caiu. */
  isStale: boolean
}

const num = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : parseFloat(v)
  return Number.isFinite(n) ? n : null
}

export function useTripLive(
  bookingId: string | null,
  destination: Coords | null,
): UseTripLiveReturn {
  const [status, setStatus] = useState<TripStatus>('nao_iniciado')
  const [position, setPosition] = useState<LivePosition | null>(null)
  const [arrivedAt, setArrivedAt] = useState<string | null>(null)
  const [eta, setEta] = useState<EtaResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [now, setNow] = useState(() => Date.now())

  const channelRef = useRef<RealtimeChannel | null>(null)

  // ── Base + polling lento ───────────────────────────────────────────────────

  useEffect(() => {
    if (!bookingId) {
      setIsLoading(false)
      return
    }
    let mounted = true

    async function refresh() {
      const { data, error } = await supabase.rpc('trip_get_live', { p_booking_id: bookingId! })
      if (!mounted) return
      if (error) {
        Sentry.captureException(error, { tags: { source: 'useTripLive.getLive' } })
        setIsLoading(false)
        return
      }
      const row = (data ?? {}) as TripLiveRow
      setStatus(row.status ?? 'nao_iniciado')
      setArrivedAt(row.arrived_at ?? null)

      const lat = num(row.position?.lat)
      const lng = num(row.position?.lng)
      if (lat !== null && lng !== null) {
        const at = row.position?.recorded_at ? Date.parse(row.position.recorded_at) : Date.now()
        // Não sobrescreve uma posição de broadcast mais recente.
        setPosition((prev) => (prev && prev.at >= at ? prev : {
          lat, lng, headingDeg: num(row.position?.heading_deg), at,
        }))
      }
      setIsLoading(false)
    }

    void refresh()
    const id = setInterval(() => void refresh(), FALLBACK_POLL_MS)
    return () => {
      mounted = false
      clearInterval(id)
    }
  }, [bookingId])

  // ── Broadcast ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!bookingId) return

    const channel = supabase
      .channel(tripChannelName(bookingId))
      .on('broadcast', { event: 'position' }, ({ payload }) => {
        const p = payload as TripBroadcastPayload
        if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lng)) return
        setPosition({
          lat: p.lat,
          lng: p.lng,
          headingDeg: p.headingDeg ?? null,
          at: p.at ?? Date.now(),
        })
      })
      .subscribe()

    channelRef.current = channel
    return () => {
      void supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [bookingId])

  // ── ETA ────────────────────────────────────────────────────────────────────

  // O broadcast chega a cada 5 s, mas recalcular o ETA nessa frequência
  // desperdiçaria request da Mapbox. Arredondar para ~100 m faz o efeito só
  // reagir a deslocamento significativo.
  const etaLatKey = position ? Math.round(position.lat * 1000) : null
  const etaLngKey = position ? Math.round(position.lng * 1000) : null

  useEffect(() => {
    if (!position || !destination || (status !== 'a_caminho' && status !== 'chegou')) {
      setEta(null)
      return
    }
    const controller = new AbortController()
    let mounted = true

    async function compute() {
      const result = await getEta(
        { lat: position!.lat, lng: position!.lng },
        destination!,
        controller.signal,
      )
      if (mounted) setEta(result)
    }

    void compute()
    const id = setInterval(() => void compute(), ETA_REFRESH_MS)
    return () => {
      mounted = false
      controller.abort()
      clearInterval(id)
    }
    // Depende das chaves arredondadas, não de `position` inteiro — ver acima.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, destination?.lat, destination?.lng, etaLatKey, etaLngKey])

  // ── Detecção de sinal velho ────────────────────────────────────────────────

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000)
    return () => clearInterval(id)
  }, [])

  const isStale = position !== null && now - position.at > 60_000

  return { status, position, eta, arrivedAt, isLoading, isStale }
}
